import { isBenchSlot, isIrSlot, slotBase, slotsForBase } from '@/lib/nflRosterSlots';

/**
 * SAB-955 — which positions can this league actually START?
 *
 * The client half of `isRosterablePosition` in `supabase/functions/_shared/
 * nfl-slot-eligibility.ts`. The rule is startability, not draftability: a bench slot holds
 * a player you can start LATER, so a position that can never start is a wasted pick and is
 * not offered at all. `nfl_acquire_pick_lock` refuses one with POSITION_NOT_ROSTERABLE.
 *
 * ── WHY THIS READS THE TABLE (SAB-812) ─────────────────────────────────────
 *
 * `nfl_slot_eligibility` is the table `nfl_acquire_pick_lock` itself reads. Using anything
 * else would mean the pool and the writer could disagree about what is legal.
 *
 * This header used to warn against `NFL_SLOT_POSITION_MAP` in `lib/nflSlots.ts`, whose IDP
 * set omitted MLB, NT, OLB and SAF. SAB-1051 corrected those values; SAB-812 deleted the
 * constant outright, because a corrected copy is still a copy. That file's only remaining
 * consumer now calls `canPlaySlotWith` below.
 */

export interface SlotEligibilityRow {
  position: string;
  slot_base: string;
}

/**
 * The set of positions with at least one STARTING slot in this league.
 *
 * A position absent from `rows` is absent from the result, matching the writer's
 * NO_SLOT_MAPPING_FOR_POSITION rather than silently benching it.
 *
 * Returns null when `rows` is null or EMPTY — "not loaded", which callers must treat as
 * "do not filter" rather than "nothing is startable".
 *
 * Empty is deliberately lumped in with null. `nfl_slot_eligibility` is a seeded reference
 * table that is never legitimately empty, so [] means the read failed, RLS denied it, or
 * it has not arrived — and taking it at face value would filter EVERY player out and leave
 * a manager staring at an empty pool on a running clock. The server takes the same stance
 * for the same reason: `loadSlotEligibility` THROWS on an empty table rather than
 * degrading to "no eligibility", which is the failure that caused SAB-739.
 *
 * This is the one place where an empty result must not be read as an answer.
 */
export function startablePositions(
  rows: SlotEligibilityRow[] | null | undefined,
  rosterSlots: unknown,
): Set<string> | null {
  if (!rows || rows.length === 0) return null;
  const out = new Set<string>();
  for (const r of rows) {
    if (slotsForBase(r.slot_base, rosterSlots).length > 0) out.add(r.position);
  }
  return out;
}

/**
 * Is this position draftable in this league?
 *
 * `startable === null` means the eligibility rows have not loaded; everything is allowed
 * so the pool never renders empty on a slow read. See startablePositions.
 */
export function isPositionDraftable(
  position: string,
  startable: ReadonlySet<string> | null,
): boolean {
  return startable === null || startable.has(position);
}

/**
 * Drop players whose position this league can never start.
 *
 * ⚠️ MUST RUN ON UNMASKED ROWS, BEFORE Guess Who masking.
 *
 * `maskPoolRow` rewrites `position` to MASKED_POSITION ("???"), so a startability check
 * applied after masking matches nothing and empties the ENTIRE pool for as long as the
 * card is active. I put this inside `filterDraftPool` first, which receives already-masked
 * rows by design (mask-then-filter, so search cannot match a hidden player's real name),
 * and four Guess Who e2e specs failed with "the player pool rendered no rows".
 *
 * Hence a separate function over raw player rows rather than an option on the filter: the
 * ordering requirement is then structural rather than a comment someone has to remember.
 *
 * `startable === null` means not loaded -- returns the list untouched.
 */
export function excludeUnrosterable<T extends { position: string }>(
  players: T[],
  startable: ReadonlySet<string> | null,
): T[] {
  if (startable === null) return players;
  return players.filter((p) => startable.has(p.position));
}

/**
 * Can this position occupy this slot? SAB-812 — the row-driven replacement for
 * `nflSlots.canPlaySlot`, which was a hand-maintained copy of the same table.
 *
 * ── WHY UNLOADED ROWS PERMIT ────────────────────────────────────────────────
 *
 * Same stance as `startablePositions`, for the same reason and with a stronger backstop:
 * this is a client PRE-CHECK, and `nfl-lineup` re-derives the identical rule from the same
 * table before writing anything. So a permissive answer here costs a round-trip and a clear
 * server refusal, while a restrictive one greys out legal slots with no explanation — the
 * shape of SAB-1051, where 369 players could not be placed in slots they were eligible for.
 *
 * Bench and IR accept any position and are answered without consulting the table at all:
 * they are universal by construction and deliberately have no rows (see the SAB-739 seed).
 * An unrecognised slot base matches no row and is refused, which preserves the old
 * constant's behaviour for a slot this league does not define.
 */
export function canPlaySlotWith(
  rows: SlotEligibilityRow[] | null | undefined,
  playerPosition: string,
  slot: string,
): boolean {
  if (isBenchSlot(slot) || isIrSlot(slot)) return true;
  if (!rows || rows.length === 0) return true;
  const base = slotBase(slot);
  return rows.some((r) => r.slot_base === base && r.position === playerPosition);
}

/**
 * The positions that map to an IDP slot, or `null` when the rows have not loaded.
 *
 * SAB-812: replaces the `IDP_POSITIONS` constant in `nflDraftBoardColors.ts`. That set had
 * already drifted once (SAB-806 — it omitted SAF/OLB/NT/MLB while listing S/SS, hiding 332
 * players behind the draft pool's IDP tab) and was corrected by hand rather than derived.
 *
 * League-independent on purpose: this answers "is this position an IDP", not "can this
 * league start one". `startablePositions` already answers the second question, and mixing
 * them would make the pool's IDP tab vanish for a league mid-load.
 */
export function idpPositions(
  rows: SlotEligibilityRow[] | null | undefined,
): Set<string> | null {
  if (!rows || rows.length === 0) return null;
  return new Set(rows.filter((r) => r.slot_base === 'IDP').map((r) => r.position));
}
